// Lazy loader for the payment provider's checkout overlay.
//
// Loaded at runtime rather than from public/index.html, which has no external
// <script> tags: adding one there would put a blocking request on every page
// to serve a feature used on one. GA4 (utils/analytics.ts) follows the same
// rule.
//
// Everything here is idempotent. The tag is injected once, the load promise is
// memoized, and Initialize runs once per page. A second call from a second buy
// button awaits the same promise. A failed load is the exception: it clears
// the promise and removes its tag, so the next buy click retries from scratch
// instead of replaying the failure until a full page reload.
//
// The overlay will not open until the CSP allows it: cdn.paddle.com in
// script-src, *.paddle.com in frame-src and connect-src. That lives in
// template.yaml and is served by CloudFront, so `npm start` (which serves no
// CSP) will work while the deployed site does not. See
// backend/tests/unit/test_template_csp.py.

import { getPaddleConfig } from './amplifyConfig';

const SCRIPT_SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';

export interface CheckoutEvent {
  name: string;
  data?: unknown;
}

interface PaddleGlobal {
  Environment: { set: (env: string) => void };
  Initialize: (options: {
    token: string;
    eventCallback?: (event: CheckoutEvent) => void;
  }) => void;
  Checkout: {
    open: (options: Record<string, unknown>) => void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

let loadPromise: Promise<PaddleGlobal> | null = null;
let initialized = false;
let eventHandler: ((event: CheckoutEvent) => void) | null = null;

// Registered separately from initialization because Initialize runs once per
// page while the component that cares about checkout.completed mounts and
// unmounts. The handler is swapped rather than re-initializing.
export const setCheckoutEventHandler = (
  handler: ((event: CheckoutEvent) => void) | null
): void => {
  eventHandler = handler;
};

const injectScript = (): Promise<PaddleGlobal> =>
  new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve(window.Paddle);
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    );

    const onLoad = () => {
      if (window.Paddle) {
        resolve(window.Paddle);
      } else {
        reject(new Error('Checkout script loaded but exposed no global'));
      }
    };

    if (existing) {
      // A tag still in flight with no promise to await, e.g. one left behind
      // by resetPaddleLoaderForTests. A failed tag never reaches here:
      // loadPaddle removes it on rejection, because a dead tag fires no
      // further events and this promise would never settle.
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', () =>
        reject(new Error('Checkout script failed to load'))
      );
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', () =>
      reject(new Error('Checkout script failed to load'))
    );
    document.head.appendChild(script);
  });

// The checkout environment from config, trimmed and lowercased, so 'Sandbox'
// or ' sandbox' from a hand-edited env file still selects sandbox. Anything
// but sandbox, live or empty is a misconfiguration: it is warned about and
// treated as live, the provider's default when no environment is set.
const normaliseEnvironment = (raw: string): string => {
  const environment = raw.trim().toLowerCase();
  if (environment !== 'sandbox' && environment !== 'live' && environment !== '') {
    console.warn(
      `Unrecognised REACT_APP_PADDLE_ENVIRONMENT "${raw}"; expected "sandbox" or "live". Using live.`
    );
  }
  return environment;
};

// Load and initialize, resolving with the ready global.
//
// Rejects when purchasing is not configured for this build. Callers should
// check isPaddleConfigured() before offering a buy button rather than relying
// on the rejection, so an unconfigured build shows an explanation instead of
// a button that errors.
export const loadPaddle = async (): Promise<PaddleGlobal> => {
  const config = getPaddleConfig();
  if (!config) {
    throw new Error('Checkout is not configured');
  }

  if (!loadPromise) {
    // Covers both rejections: the tag's error event and a load that exposed
    // no global. Either way the tag is spent, so it goes with the promise.
    loadPromise = injectScript().catch((err: unknown) => {
      loadPromise = null;
      document.querySelector(`script[src="${SCRIPT_SRC}"]`)?.remove();
      throw err;
    });
  }

  const paddle = await loadPromise;

  if (!initialized) {
    // Sandbox must be set BEFORE Initialize. Omitting it entirely is what
    // selects live, so this is never called with 'live' as an argument.
    if (normaliseEnvironment(config.environment) === 'sandbox') {
      paddle.Environment.set('sandbox');
    }
    paddle.Initialize({
      token: config.token,
      eventCallback: (event: CheckoutEvent) => {
        if (eventHandler) {
          eventHandler(event);
        }
      },
    });
    initialized = true;
  }

  return paddle;
};

export const isPaddleConfigured = (): boolean => getPaddleConfig() !== null;

export interface OpenCheckoutOptions {
  priceId: string;
  email: string;
  userId: string;
  discountId?: string;
}

// Open the overlay for one price.
//
// The buyer's Cognito sub travels in customData rather than as a customer id:
// the provider's customer field accepts only its own ctm_ identifiers. The
// backend treats that sub as a routing hint and never as proof of identity,
// falling back to a lookup on the customer reference when it is absent or
// malformed.
export const openCheckout = async (options: OpenCheckoutOptions): Promise<void> => {
  const paddle = await loadPaddle();

  const payload: Record<string, unknown> = {
    items: [{ priceId: options.priceId, quantity: 1 }],
    // Prefilled and locked: the purchase must land on the account that is
    // signed in, and allowLogout would let someone switch identity mid-flow
    // and produce a purchase nobody can be credited for.
    customer: { email: options.email },
    customData: { userId: options.userId },
    settings: { allowLogout: false },
  };

  // discountId or discountCode, never both. Only attached when the account is
  // eligible; what the overlay does with an ineligible discount is
  // undocumented, so the decision stays on our side.
  if (options.discountId) {
    payload.discountId = options.discountId;
  }

  paddle.Checkout.open(payload);
};

// Test seam. The module-level memoization is the point in a browser and a
// liability across tests.
export const resetPaddleLoaderForTests = (): void => {
  loadPromise = null;
  initialized = false;
  eventHandler = null;
};
